import { MouseEvent } from "react";

import { GeneralSvgPointDto } from "@/core/api/generated/v1";

import {
  SvgAttributes,
  SvgDimensions,
  SvgPointCoords,
  SvgPointFullCoords,
  SvgViewBox,
} from "../ts/svg";
import { FileHelper } from "./file";
import { UrlHelper } from "./url";

export const SvgPointCoordinatesMode = {
  /** SVG absolute/native coordinates (match the viewBox). */
  Absolute: "Absolute",
  /** SVG relative/normalized coordinates (0–1 range, relative to the viewBox). */
  Relative: "Relative",
} as const;
export type SvgPointCoordinatesMode =
  (typeof SvgPointCoordinatesMode)[keyof typeof SvgPointCoordinatesMode];

export class SvgHelper {
  // private static readonly _logPrefix = "SvgHelper";

  public static isPathElement(pathEl: Nil<Element>): pathEl is SVGPathElement {
    return !!pathEl && pathEl instanceof SVGPathElement && pathEl.nodeName === "path";
  }

  public static removeWidthAndHeightAttributesFromSvgContent(svg: string): string {
    const newSvg = svg.replace(/(?<svgTagInfo><svg(\s*[a-zA-Z:]+=".*")+>)/, (svgTagInfo) => {
      const tagFixed = svgTagInfo.replace(/(width|height)+=".*"/g, "");
      return tagFixed;
    });
    return newSvg;
  }

  public static isSvgDataUrl(dataUrl: string): boolean {
    return UrlHelper.isDataUrlWithMimeType(dataUrl, "image/svg+xml");
  }

  public static isSvgDataUrlOrThrow(dataUrl: string): boolean {
    if (!this.isSvgDataUrl(dataUrl)) {
      throw new Error(`Invalid SVG Data URL: ${dataUrl}`);
    }
    return true;
  }

  public static dataUrlToSvgString(dataUrl: string): string {
    this.isSvgDataUrlOrThrow(dataUrl);
    const dataUrlParsed = UrlHelper.parseDataUrl(dataUrl);
    const svgStr = dataUrlParsed.dataBase64Decoded;
    if (!svgStr) {
      throw new Error("Failed to decode SVG data URL.");
    }
    return svgStr;
  }

  public static dataUrlToSVGElement(dataUrl: string): SVGSVGElement {
    this.isSvgDataUrlOrThrow(dataUrl);
    const svgStr = SvgHelper.dataUrlToSvgString(dataUrl);
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgStr, "image/svg+xml");
    const svgElement = svgDoc.querySelector("svg");
    if (!svgElement) {
      throw new Error("Failed to get SVGSVGElement from data URL.");
    }
    return svgElement;
  }

  public static SVGElementToSvgString(svgEl: SVGSVGElement): string {
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgEl);
  }

  public static SVGElementToBlob(svgEl: SVGSVGElement): Blob {
    const svgString = this.SVGElementToSvgString(svgEl);
    return FileHelper.stringToBlob(svgString, "image/svg+xml");
  }

  public static SVGElementToDataUrl(svgEl: SVGSVGElement): string {
    const svgString = this.SVGElementToSvgString(svgEl);
    return UrlHelper.stringToDataUrlInBase64(svgString, "image/svg+xml");
  }

  public static SVGElementToDataUrlAsync(svgEl: SVGSVGElement): Promise<string> {
    const blob = this.SVGElementToBlob(svgEl);
    return FileHelper.blobToDataUrl(blob);
  }

  public static parseDimensions(svgEl: SVGSVGElement): SvgDimensions {
    const viewBox = this.parseViewBox(svgEl);
    const width = Number(svgEl.getAttribute("width") || viewBox.width);
    const height = Number(svgEl.getAttribute("height") || viewBox.height);
    return { width, height };
  }

  public static parseViewBox(svgEl: SVGSVGElement): SvgViewBox {
    const viewBoxAttr = svgEl.getAttribute("viewBox");
    if (viewBoxAttr) {
      const parts = viewBoxAttr.split(/\s+|,/).map(Number);
      if (parts.length !== 4) {
        throw new Error(`Invalid viewBox attribute: ${viewBoxAttr}`);
      }
      const [minX, minY, width, height] = parts;
      return { viewBox: viewBoxAttr, minX, minY, width, height };
    }

    // fallback: width/height attributes
    const minX = 0;
    const minY = 0;
    const width = Number(svgEl.getAttribute("width") || 0);
    const height = Number(svgEl.getAttribute("height") || 0);
    return { viewBox: `${minX} ${minY} ${width} ${height}`, minX, minY, width, height };
  }

  public static parseAttributes(svgEl: SVGSVGElement): SvgAttributes {
    const viewBox = this.parseViewBox(svgEl);
    const width = Number(svgEl.getAttribute("width") || viewBox.width);
    const height = Number(svgEl.getAttribute("height") || viewBox.height);
    return { viewBox, width, height };
  }

  public static setDimensions(svgEl: SVGSVGElement, dimensions: SvgDimensions): void {
    svgEl.setAttribute("width", dimensions.width.toString());
    svgEl.setAttribute("height", dimensions.height.toString());
  }

  public static setViewBox(svgEl: SVGSVGElement, viewBox: Omit<SvgViewBox, "viewBox">): void {
    svgEl.setAttribute(
      "viewBox",
      `${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`,
    );
  }

  public static convertAbsoluteToRelativeCoords({
    viewBox,
    coords,
  }: {
    viewBox: SvgViewBox;
    coords: SvgPointCoords;
  }): SvgPointCoords {
    return {
      ...coords,
      x: (coords.x - viewBox.minX) / viewBox.width,
      y: (coords.y - viewBox.minY) / viewBox.height,
    };
  }

  public static convertRelativeToAbsoluteCoords({
    viewBox,
    coords,
  }: {
    viewBox: SvgViewBox;
    coords: SvgPointCoords;
  }): SvgPointCoords {
    return {
      ...coords,
      x: viewBox.minX + coords.x * viewBox.width,
      y: viewBox.minY + coords.y * viewBox.height,
    };
  }

  /** Returns mouse cursor coordinates on SVG viewBox. */
  public static getCursorPointCoords(
    e: MouseEvent<SVGSVGElement | SVGCircleElement, globalThis.MouseEvent>,
  ): SvgPointFullCoords | null {
    const targetEl = e.target as Element | null;
    const svgElement = targetEl?.closest("svg");
    if (!svgElement) {
      return null;
    }

    const viewBox2 = SvgHelper.parseViewBox(svgElement);

    const svgMatrix = svgElement.getScreenCTM()?.inverse();
    if (!svgMatrix) {
      return null;
    }

    const domPoint = new DOMPoint();
    domPoint.x = e.pageX;
    domPoint.y = e.pageY;

    const domPointTransformed = domPoint.matrixTransform(svgMatrix);

    // const refBBox = svgElement.getBBox();
    // const pointCoordsOld: SvgPointCoords = {
    //   x: domPointTransformed.x,
    //   y: domPointTransformed.y,
    //   xp: domPointTransformed.x / (refBBox.width + refBBox.x * 2),
    //   yp: domPointTransformed.y / (refBBox.height + refBBox.y * 2),
    //   width: parseInt((refBBox.width + refBBox.x * 2).toFixed(0)),
    //   height: parseInt((refBBox.height + refBBox.y * 2).toFixed(0)),
    // };

    const absoluteCoords: SvgPointCoords = {
      x: domPointTransformed.x,
      y: domPointTransformed.y,
    };
    const relativeCoords: SvgPointCoords = this.convertAbsoluteToRelativeCoords({
      viewBox: viewBox2,
      coords: absoluteCoords,
    });
    const pointCoords: SvgPointFullCoords = {
      x: absoluteCoords.x,
      y: absoluteCoords.y,
      xp: relativeCoords.x,
      yp: relativeCoords.y,
      width: viewBox2.width,
      height: viewBox2.height,
    };

    // console.log(this._logPrefix, "getCursorPointCoords", {
    //   e,
    //   eCoords: {
    //     clientX: e.clientX,
    //     clientY: e.clientY,
    //     pageX: e.pageX,
    //     pageY: e.pageY,
    //     screenX: e.screenX,
    //     screenY: e.screenY,
    //     movementX: e.movementX,
    //     movementY: e.movementY,
    //   },
    //   svgElement,
    //   viewBox2,
    //   svgMatrix,
    //   refBBox,
    //   domPoint,
    //   domPointTransformed,
    //   height: viewBox2.height,
    //   pointCoordsOld,
    //   pointCoords,
    //   temp: {
    //     width: parseInt(refBBox.width.toFixed(0)),
    //     height: parseInt(refBBox.height.toFixed(0)),
    //   },
    // });

    return pointCoords;
  }

  /** Checks that coords are inside the SVG viewBox.
   If SVG element is stretched via CSS (e.g. width/height), it might add extra space around the SVG content,
   so we need to check if the point is within the viewBox bounds,
   otherwise we might add a point with negative coords that is outside of the SVG content. 
  */
  public static isPointInsideViewBox({
    viewBox,
    coords,
  }: {
    viewBox: SvgViewBox;
    coords: SvgPointFullCoords;
  }): boolean {
    return (
      coords.x >= 0 && coords.x <= viewBox.width && coords.y >= 0 && coords.y <= viewBox.height
    );
  }

  // #region Mapping

  public static mapGeneralSvgPointDtoToSvgPointFullCoords(
    source: GeneralSvgPointDto,
  ): SvgPointFullCoords {
    return {
      x: source.x ?? 0,
      y: source.y ?? 0,
      xp: source.xp ?? 0,
      yp: source.yp ?? 0,
      width: source.width ?? 0,
      height: source.height ?? 0,
    };
  }

  public static mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(
    source: Nil<GeneralSvgPointDto>,
  ): SvgPointFullCoords | null {
    if (!source) {
      return null;
    }
    return this.mapGeneralSvgPointDtoToSvgPointFullCoords(source);
  }

  public static mapSvgPointFullCoordsToGeneralSvgPointDto(
    source: SvgPointFullCoords,
  ): GeneralSvgPointDto {
    return {
      ...source,
    };
  }

  // #endregion Mapping
}
